import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getPaginatedPartCategories,
  getPartCategoriesByIds,
  getPartCategoriesBySearch,
  getPartCategory,
} from "@/api/resources/partCategories/api";
import i18n from "@/providers/i18n";
import { type QueryPaginationFilters } from "@/utils/pagination";

import type { SearchOptionFilters } from "@/utils/api";

const entity = i18n.t("part-category");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("part-categories");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const partCategoriesQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "partCategories"],
  lists: () => [...partCategoriesQueryKeys.all(), "list"],
  listByIds: (ids?: string[]) => [...partCategoriesQueryKeys.lists(), "byIds", ids],
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) => [
    ...partCategoriesQueryKeys.lists(),
    "paginated",
    queryKeyPaginationFilters,
  ],
  listBySearch: (searchOptionFilters: SearchOptionFilters) => [
    ...partCategoriesQueryKeys.lists(),
    "bySearch",
    searchOptionFilters,
  ],
  byId: (id?: string) => [...partCategoriesQueryKeys.all(), "byId", id],
};
export const partCategoriesQueries = {
  listByIds: (ids?: string[]) =>
    queryOptions({
      queryKey: [...partCategoriesQueryKeys.listByIds(ids)],
      queryFn: () =>
        getPartCategoriesByIds({
          keyIn: "id",
          idsIn: ids,
        }),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listPaginated: (queryKeyPaginationFilters: QueryPaginationFilters) =>
    queryOptions({
      queryKey: [...partCategoriesQueryKeys.listPaginated(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedPartCategories(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listBySearch: (searchOptionFilters: SearchOptionFilters) =>
    queryOptions({
      queryKey: [...partCategoriesQueryKeys.listBySearch(searchOptionFilters)],
      queryFn: () => getPartCategoriesBySearch(searchOptionFilters),
      placeholderData: (prev) => prev, // ! this prevents flashing of results when using search in selectors
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...partCategoriesQueryKeys.byId(id)],
      queryFn: id ? () => getPartCategory({ match: { id } }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
